import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import CEOPhoto from '../assets/CEO.jpg';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    els.forEach(el => el.setAttribute('data-reveal-init', ''));
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => io.observe(el));
    const safety = setTimeout(() => els.forEach(el => el.classList.add('in')), 1500);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, []);
}

export default function MeetOurCEOPage() {
  useReveal();

  return (
    <main style={{ paddingTop: 100 }}>

      {/* ── Back link ── */}
      <div className="page-container" style={{ paddingTop: 32 }}>
        <Link
          to="/about"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'var(--sans)',
            fontSize: 11,
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: 'var(--ash)',
            textDecoration: 'none',
          }}>
          <ArrowLeft size={12} /> Back to About Us
        </Link>
      </div>

      {/* ── Hero ── */}
      <section style={{ padding: '48px 0 0' }}>
        <div className="page-container">
          <p className="eyebrow eyebrow-gold animate-fade-up" style={{ marginBottom: 16 }}>
            Meet Our CEO
          </p>
          <h1
            className="animate-fade-up delay-1"
            style={{
              fontFamily: 'var(--serif)',
              fontSize: 'clamp(44px, 7.5vw, 100px)',
              lineHeight: 0.98,
              textWrap: 'balance',
              maxWidth: 1000,
            }}>
            Robert D. Williams
          </h1>
          <p
            className="animate-fade-up delay-2"
            style={{
              fontFamily: 'var(--sans)',
              fontSize: 11,
              letterSpacing: '.22em',
              textTransform: 'uppercase',
              color: 'var(--ash)',
              marginTop: 16,
            }}>
            Founder &amp; President — Love3 Products, Inc.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 56,
              marginTop: 64,
              alignItems: 'start',
            }}>

            {/* Biography */}
            <div data-reveal style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 17, color: 'var(--ink-soft)', lineHeight: 1.75 }}>
                Robert D. Williams is the founder and president of Love3 Products, Inc. He is a successful
                real estate developer, tech entrepreneur, and international wholesale merchant.
              </p>

              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.75 }}>
                Upon completion of his undergraduate studies at Florida A&amp;M University (B.S. Civil
                Engineering), Robert received a commission in the United States Navy as a Submarine Nuclear
                Propulsion Officer. While in the service of his country, Robert was an engineering division
                officer and participated in several strategic deterrent missions in the Atlantic Ocean. His
                final assignment was as an Individual Augmentee at the Combined Joint Taskforce Kabul,
                Afghanistan, where he served as Chief of Staff for Commanding General Karl Eikenberry's
                Political Advisor.
              </p>

              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.75 }}>
                Robert received a Joint Commendation Medal for his efforts in Afghanistan and was Honorably
                Discharged in 2007.
              </p>

              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.75 }}>
                At the completion of his military service, Robert obtained his commercial general
                contractor's license and founded an Atlanta-based construction firm. They successfully
                completed government, commercial, and multifamily projects. He soon transitioned into real
                estate development and obtained a portfolio of affordable housing multifamily communities.
              </p>

              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.75 }}>
                Through his real estate development endeavors, Robert realized the need to develop a
                product-based business that would provide capital for future developments. He began trading
                emeralds from Colombia, South America, and frozen foods in Sierra Leone, West Africa. He
                quickly recognized the vast opportunities within the global supply chain and decided to
                fully immerse himself in it. Robert relocated to Southeast Asia and began developing product
                lines for the American market—including Contractor Rob Flooring.
              </p>

              <blockquote
                style={{
                  fontFamily: 'var(--serif)',
                  fontStyle: 'italic',
                  fontSize: 22,
                  color: 'var(--ink)',
                  borderLeft: '3px solid var(--saffron)',
                  paddingLeft: 24,
                  marginTop: 16,
                  lineHeight: 1.4,
                }}>
                "The mission of Love3 Products is to spread love throughout the world by providing premium
                quality products, excellent customer service, and fair pricing. Starting with our leadership
                team and transcending all aspects of our company, we lead with love to better serve our
                customers and the greater community."
              </blockquote>
            </div>

            {/* Portrait */}
            <div
              data-reveal
              style={{
                aspectRatio: '3/4',
                borderRadius: 'var(--r-md)',
                overflow: 'hidden',
                background: 'var(--linen)',
              }}>
              <img
                src={CEOPhoto}
                alt="Robert D. Williams, Founder & President of Love3 Products"
                style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center top' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: '96px 0' }}>
        <div className="page-container" style={{ textAlign: 'center' }}>
          <h2
            style={{
              fontFamily: 'var(--serif)',
              fontSize: 'clamp(32px, 4.5vw, 56px)',
              textWrap: 'balance',
            }}
            data-reveal>
            Begin a project with us.
          </h2>
          <div
            className="flex justify-center gap-4"
            style={{ marginTop: 32, flexWrap: 'wrap' }}
            data-reveal>
            <Link to="/rfq/submit" className="btn btn-primary btn-lg">
              Request Wholesale Pricing <ArrowRight size={14} />
            </Link>
            <Link to="/catalog" className="btn btn-outline btn-lg">
              Browse the Catalog
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
